import { logError } from '@core/logging'
import { logInfoCollapsed } from '@core/logging/helpers'
import { getPIPIframe } from '@core/pictureInPicture/getPIPIframe'
import { hasPIPIframe } from '@core/pictureInPicture/hasPIPIframe'

import type { EventType } from './eventType'
import { postMessageWrapper } from './postMessageWrapper'

/** Remove unserializable values from object using JSON */
function filterUnserializable(obj: unknown) {
  return JSON.parse(JSON.stringify(obj))
}

export interface CustomEventTarget {
  dispatchEvent(event: CustomEvent): boolean
  host?: Node
}

interface Props {
  target?: CustomEventTarget | null
  type: EventType | EventType[] | string
  data: Record<string, unknown>
}

/**
 * Propagates data to:
 * - window and parent window as messages.
 * - iframe and current document as custom events.
 * - widget target as custom events.
 */
export function customEvent({ target, type, data }: Props): void {
  const types = typeof type !== 'object' ? [type] : type

  types.forEach((type) => {
    try {
      postMessageWrapper({
        ...filterUnserializable(data),
        type,
      })

      // Dispatch event from widget into iframe when PIP is active.
      // Isolated try/catch: a cross-origin PIP iframe throws SecurityError
      // on `.document` access; that is an expected, non-actionable condition
      // and must not break the remaining dispatches in this forEach body.
      if (hasPIPIframe()) {
        try {
          getPIPIframe()?.document.dispatchEvent(
            new CustomEvent(type, {
              detail: data,
            }),
          )
        } catch {
          // Cross-origin PIP iframe: cannot reach document directly.
        }
      }

      document.dispatchEvent(
        new CustomEvent(type, {
          detail: data,
        }),
      )

      // Dispatch on widget target (HTMLElement | ShadowRoot)
      ;(target?.host || target)?.dispatchEvent(
        new CustomEvent(type, {
          detail: data,
        }),
      )

      logInfoCollapsed(`CustomEvent ${type}`, data)
    } catch (err) {
      logError(err)
    }
  })
}
